import { FC, ReactNode, useEffect } from 'react';
import styled from 'styled-components';
import { addAlpha } from '@algoan/deep-dive/theme';
import { useDispatch } from '@algoan/dashboard/store/store';
import { NotificationDuration, NotificationStatus, NotificationVariant } from '@algoan/dashboard/model';
import { getDuration, getIcon } from './notification-utils';
import { popNotificationFinish } from '@algoan/dashboard/store/slice';

export interface NotificatonBarProps {
  variant?: NotificationVariant;
  status: NotificationStatus;
  children: ReactNode;
}

export const NotificationBar: FC<NotificatonBarProps> = ({ status, children, variant }) => {
  const dispatch = useDispatch();

  useEffect(() => {
    const timer = setTimeout(
      () => {
        if (status === NotificationStatus.FADING) dispatch(popNotificationFinish());
      },
      getDuration(status) * 1000,
    );
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  return (
    <S.Container status={status} variant={variant} data-testid="notification">
      {getIcon(variant)}
      <S.Content>{children}</S.Content>
    </S.Container>
  );
};

const S = {
  Container: styled.div<{ status: NotificationStatus; variant?: NotificationVariant }>`
    display: flex;
    align-items: center;
    position: fixed;
    right: 16px;
    top: 16px;
    box-shadow: 0px 5px 15px ${(props) => addAlpha(props.theme.algoanBlue, 0.12)};
    border-radius: 4px;
    padding: 16px 24px;
    width: 323px;
    box-sizing: border-box;
    z-index: 100000;

    ${({ variant, theme }) => {
      switch (variant) {
        case NotificationVariant.ERROR:
          return `
          background: ${theme.feedbackColors.softRed};
          color: ${theme.redNotification};
          `;
        case NotificationVariant.SUCCESS:
          return `
          background: ${theme.feedbackColors.softGreen};
          color: ${theme.feedbackColors.greenNotification};
          `;
      }
    }}

    ${({ status }) =>
      status === NotificationStatus.RENDERED
        ? `
          opacity: 1;
          visibility: visible;
          transition: opacity ${NotificationDuration.FADING}s linear;
        `
        : `
          opacity: 0;
          visibility: hidden;
          transition: visibility 0s ${NotificationDuration.FADING}s, opacity ${NotificationDuration.FADING}s linear;
        `}
  `,
  Content: styled.div`
    font-weight: 600;
    font-size: 13px;
    line-height: 16px;
    margin-left: 15px;
    white-space: break-spaces;
  `,
};
